import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import type {
  ApiErrorBody,
  AuthSession,
  BundleDetail,
  BundleListFilters,
  BundleSummary,
  Order,
  Paginated,
  Region,
} from '@/types';

/**
 * Centralized HTTP layer (mirrors @bundle/api-client). Enforces the security
 * model uniformly:
 *  - Authorization: Bearer <15min in-memory access token> on authed calls
 *  - x-csrf-token on EVERY non-GET request
 *  - single-flight refresh on 401 -> retry once -> onUnauthorized on 2nd fail
 *
 * Refresh tokens are NEVER touched here: the 30d httpOnly SameSite=Lax cookie
 * is owned by the BFF proxy routes (/api/auth/login|refresh|logout), called
 * with credentials.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiErrorBody | null,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** First human-readable message from a NestJS validation/error payload. */
  static fromAxios(err: AxiosError<ApiErrorBody>): ApiError {
    const status = err.response?.status ?? 0;
    const body = err.response?.data ?? null;
    const raw = body?.message;
    const message = Array.isArray(raw) ? raw[0] : raw ?? err.message;
    return new ApiError(status, body, message);
  }
}

export interface AuthCallbacks {
  getAccessToken: () => string | null;
  getCsrfToken: () => string | null;
  /** Force-refresh via the BFF; resolves with a fresh session or throws. */
  ensureAccessToken: (force?: boolean) => Promise<AuthSession | null>;
  /** Terminal: clears the session and routes to login. */
  onUnauthorized: () => void;
}

const RETRY_FLAG = Symbol('retried');
type RetryConfig = InternalAxiosRequestConfig & { [RETRY_FLAG]?: boolean };

let callbacks: AuthCallbacks | null = null;

/** Wired once at app bootstrap from the Zustand auth store. */
export function configureApiAuth(cb: AuthCallbacks): void {
  callbacks = cb;
}

// 동일 출처(sb.absol.kr) 프록시가 /v1 을 API(NestJS, 글로벌 프리픽스 /v1)로 전달합니다.
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/v1';

export const http: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // send the httpOnly refresh cookie to the BFF
  timeout: 20_000,
  headers: { 'Content-Type': 'application/json' },
});

// --- request: attach bearer + csrf ----------------------------------------
http.interceptors.request.use((config) => {
  const token = callbacks?.getAccessToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  const method = (config.method ?? 'get').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') {
    const csrf = callbacks?.getCsrfToken();
    if (csrf) config.headers.set('x-csrf-token', csrf);
  }
  return config;
});

// --- response: single-flight refresh on 401 -------------------------------
let refreshPromise: Promise<AuthSession | null> | null = null;

http.interceptors.response.use(
  (res) => res,
  async (error: AxiosError<ApiErrorBody>) => {
    const original = error.config as RetryConfig | undefined;

    if (
      error.response?.status === 401 &&
      original &&
      !original[RETRY_FLAG] &&
      callbacks
    ) {
      original[RETRY_FLAG] = true;
      try {
        // Share one in-flight refresh across all concurrent 401s.
        refreshPromise ??= callbacks.ensureAccessToken(true).finally(() => {
          refreshPromise = null;
        });
        const session = await refreshPromise;
        if (session) {
          original.headers.set('Authorization', `Bearer ${session.accessToken}`);
          return http(original);
        }
      } catch {
        /* fall through to logout */
      }
      callbacks.onUnauthorized();
    }

    return Promise.reject(ApiError.fromAxios(error));
  },
);

async function request<T>(config: AxiosRequestConfig): Promise<T> {
  const res = await http.request<unknown>(config);
  const body = res.data as unknown;
  // API는 모든 응답을 { success, data, correlationId, timestamp } 로 감쌉니다.
  // 컴포넌트는 안쪽 data 만 다루므로 여기서 언래핑합니다.
  if (
    body &&
    typeof body === 'object' &&
    'success' in body &&
    'data' in body
  ) {
    return (body as { data: T }).data;
  }
  return body as T;
}

// --- per-resource modules (tree-shakeable, no monolith) --------------------

// API user는 { name } 을 내려주지만 web 컴포넌트는 displayName 을 씁니다 → 정규화.
function normalizeSession(s: AuthSession): AuthSession {
  const u = s.user as unknown as Record<string, any>;
  if (u && u.displayName == null && u.name != null) u.displayName = u.name;
  // API는 role(단수 문자열)을 내려주지만 web 컴포넌트는 roles[] 를 씁니다 → 정규화.
  if (u && (!Array.isArray(u.roles) || u.roles.length === 0)) {
    u.roles = u.role != null ? [u.role] : ['USER'];
  }
  return s;
}

export const authApi = {
  login: async (body: { email: string; password: string }) =>
    normalizeSession(
      await request<AuthSession>({ url: '/auth/login', method: 'POST', data: body }),
    ),
  signup: async (body: { email: string; password: string; displayName: string }) =>
    // API RegisterDto는 `name`을 받습니다(`displayName`은 화이트리스트 밖 → 400).
    normalizeSession(
      await request<AuthSession>({
        url: '/auth/signup',
        method: 'POST',
        data: { email: body.email, password: body.password, name: body.displayName },
      }),
    ),
  refresh: () =>
    request<AuthSession>({ url: '/auth/refresh', method: 'POST' }),
  logout: () => request<void>({ url: '/auth/logout', method: 'POST' }),
  me: () => request<AuthSession['user']>({ url: '/auth/me', method: 'GET' }),
  oauthUrl: (provider: 'kakao' | 'google' | 'naver') =>
    `${BASE_URL}/auth/oauth/${provider}`,
};

// API 번들 응답(서버 DTO)을 web 컴포넌트가 기대하는 BundleSummary 형태로 변환.
// 서버는 title 을 문자열로, 지역을 regionId 로, 인원을 participations 로 내려줍니다.
function stableLock(id: string): number {
  let n = 0;
  for (const ch of String(id)) n = (n * 31 + ch.charCodeAt(0)) % 100000;
  return n;
}

/** 결정적 실사 플레이스홀더(picsum.photos). loremflickr 가 불안정(5xx)하여 대체. */
function placeholderImg(seed: string | number, w = 600, h = 450): string {
  return `https://picsum.photos/seed/moa-${seed}/${w}/${h}`;
}

/**
 * 외부 이미지 URL 안전화: 불안정한 loremflickr URL 은 동일 시드의 picsum 으로 치환.
 * (재시드 전 기존 DB 데이터도 즉시 정상 표시되도록 어댑터에서 방어)
 */
function safeImg(url: string | null | undefined, fallbackSeed: string | number, w = 800, h = 800): string {
  if (!url) return placeholderImg(fallbackSeed, w, h);
  if (url.includes('loremflickr.com')) return placeholderImg(stableLock(url), w, h);
  return url;
}

function mapBundleSummary(b: Record<string, any>): BundleSummary {
  const participations = b.participations ?? {};
  const regionName =
    typeof b.region?.name === 'object'
      ? b.region.name
      : { ko: b.region?.name ?? '', en: '' };
  // 실사 썸네일: 서버 이미지가 있으면 안전화, 없으면 결정적 picsum 으로 대체.
  const thumb = b.thumbnailUrl
    ? safeImg(b.thumbnailUrl, stableLock(b.id), 600, 450)
    : placeholderImg(stableLock(b.id), 600, 450);
  return {
    id: b.id,
    type: b.type,
    status: b.status,
    title:
      typeof b.title === 'string'
        ? { ko: b.title, en: b.title }
        : b.title ?? { ko: '', en: '' },
    thumbnailUrl: thumb,
    unitPriceKrw: b.totalPrice ?? b.dealPrice ?? b.subtotal ?? 0,
    targetCount: participations.target ?? b.targetQuantity ?? 0,
    currentCount: participations.count ?? 0,
    region: { id: b.regionId ?? '', slug: b.regionSlug ?? '', name: regionName },
    deadlineAt:
      b.closeAt ?? b.deadlineAt ?? new Date(Date.now() + 86_400_000).toISOString(),
  } as BundleSummary;
}

export const bundlesApi = {
  list: async (filters: BundleListFilters = {}) => {
    // API의 BundleQueryDto는 화이트리스트 검증(forbidNonWhitelisted) → 허용 파라미터만 전달.
    // web의 page/pageSize/regionSlug 는 제외/매핑(regionSlug→region).
    const params: Record<string, string> = {};
    if (filters.type) params.type = filters.type;
    if (filters.status) params.status = filters.status;
    if (filters.regionSlug) params.region = filters.regionSlug;
    if (filters.tag) params.tag = filters.tag;
    const r = await request<{
      data: Record<string, any>[];
      page?: { nextCursor: string | null };
    }>({ url: '/bundles', method: 'GET', params });
    const items = (r.data ?? []).map(mapBundleSummary);
    return {
      items,
      total: items.length,
      page: 1,
      pageSize: items.length,
    } satisfies Paginated<BundleSummary>;
  },
  get: async (id: string) => {
    const b = await request<Record<string, any>>({
      url: `/bundles/${id}`,
      method: 'GET',
    });
    // 상세 페이지가 쓰는 원본 필드 + 정규화된 요약 필드를 함께 제공.
    // description 은 서버가 문자열로 주므로 localized() 가 읽도록 객체화.
    const summary = mapBundleSummary(b);
    const description =
      typeof b.description === 'string'
        ? { ko: b.description, en: b.description }
        : b.description ?? { ko: '', en: '' };
    // 상세 갤러리용 images 배열(서버 미제공 → 썸네일 실사로 채움), tags 기본값 보정.
    const images: string[] = Array.isArray(b.images) && b.images.length
      ? b.images.map((src: string) => safeImg(src, stableLock(b.id), 600, 450))
      : summary.thumbnailUrl
        ? [summary.thumbnailUrl]
        : [];
    const tags: string[] = Array.isArray(b.tags) ? b.tags : [];
    return { ...b, ...summary, description, images, tags } as unknown as BundleDetail;
  },
  join: (id: string, body: { quantity: number }) =>
    request<{ orderId: string }>({
      url: `/bundles/${id}/join`,
      method: 'POST',
      data: body,
    }),
};

export const regionsApi = {
  list: () => request<Region[]>({ url: '/regions', method: 'GET' }),
  bySlug: (slug: string) =>
    request<Region>({ url: `/regions/${slug}`, method: 'GET' }),
};

export interface CategoryNode {
  id: string;
  key: string;
  name: string;
  slug: string;
  sort?: number;
  children?: CategoryNode[];
}

export const categoriesApi = {
  list: () => request<CategoryNode[]>({ url: '/categories', method: 'GET' }),
};

export interface ProductCard {
  id: string;
  title: string;
  price: number;
  currency: string;
  imageUrls: string[];
  stock: number;
}

export const productsApi = {
  list: async (params: { limit?: number } = {}) => {
    const r = await request<{ data: ProductCard[] }>({
      url: '/products',
      method: 'GET',
      params,
    });
    // 불안정한 loremflickr 이미지를 안전화(picsum 대체).
    return (r.data ?? []).map((p) => ({
      ...p,
      imageUrls: (p.imageUrls ?? []).map((u) => safeImg(u, p.id)),
    }));
  },
};

// API OrderResponseDto → web Order. 번들 제목은 응답에 없어 첫 항목 제목으로 대체.
function mapOrder(o: Record<string, any>): Order {
  const items: any[] = o.items ?? [];
  const firstTitle = items[0]?.title ?? '번들 주문';
  const label = items.length > 1 ? `${firstTitle} 외 ${items.length - 1}건` : firstTitle;
  const qty = items.reduce((s, it) => s + (it.quantity ?? 0), 0) || items.length;
  return {
    id: o.id,
    bundleId: o.bundleId,
    bundleTitle: { ko: label, en: label },
    status: o.status,
    quantity: qty,
    totalKrw: o.amount ?? 0,
    createdAt: o.createdAt,
    carrier: o.fulfillment?.carrier ?? undefined,
    trackingNo: o.fulfillment?.trackingNo ?? undefined,
  } as unknown as Order;
}

export interface MyRoleRequest {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  currentType?: string;
  targetType?: string;
  meta?: { businessName?: string; regionId?: string; note?: string } | null;
  adminNote?: string | null;
  createdAt: string;
  resolvedAt?: string | null;
}

export const roleRequestsApi = {
  list: async (): Promise<MyRoleRequest[]> => {
    const r = await request<unknown>({ url: '/me/role-requests', method: 'GET' });
    const arr = Array.isArray(r) ? r : ((r as any)?.data ?? []);
    return arr as MyRoleRequest[];
  },
  create: (body: { businessName: string; regionId: string; note?: string }) =>
    request<MyRoleRequest>({
      url: '/me/role-requests',
      method: 'POST',
      data: { requestedRole: 'SELLER', ...body },
    }),
};

export const ordersApi = {
  listMine: async (_params: { page?: number; pageSize?: number } = {}) => {
    // API는 cursor 방식({data,page}). web은 {items,total} 기대 → 매핑.
    const r = await request<{ data: Record<string, any>[]; page?: unknown }>({
      url: '/me/orders',
      method: 'GET',
    });
    const items = (r.data ?? []).map(mapOrder);
    return { items, total: items.length, page: 1, pageSize: items.length } satisfies Paginated<Order>;
  },
  get: async (id: string) =>
    mapOrder(await request<Record<string, any>>({ url: `/me/orders/${id}`, method: 'GET' })),
  cancel: async (id: string) =>
    mapOrder(await request<Record<string, any>>({ url: `/me/orders/${id}/cancel`, method: 'POST' })),
};
